import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Eye, RefreshCw, AlertCircle } from "lucide-react";
import { StatusPill, SlideOver, StatsRow } from "@/components/phase4/Phase4Common";

const REASONS = ["DAMAGED", "LOST", "PARTIAL_LOSS", "WRONG_DELIVERY", "THEFT"];

export default function InsuranceClaims() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterReason, setFilterReason] = useState("");
  const [stats, setStats] = useState({ submitted: 0, review: 0, approved: 0, rejected: 0, paid: 0 });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 100 };
      if (filterStatus) params.status = filterStatus;
      const r = await api.get("/v2/insurance/claims", { params });
      let list = r.data?.items || [];
      if (filterReason) list = list.filter((c) => c.claim_reason === filterReason);
      setItems(list);
      const all = await api.get("/v2/insurance/claims", { params: { page_size: 1000 } });
      const allItems = all.data?.items || [];
      setStats({
        submitted: allItems.filter((c) => c.status === "SUBMITTED").length,
        review: allItems.filter((c) => c.status === "UNDER_REVIEW").length,
        approved: allItems.filter((c) => ["APPROVED", "PARTIALLY_APPROVED"].includes(c.status)).length,
        rejected: allItems.filter((c) => c.status === "REJECTED").length,
        paid: allItems
          .filter((c) => ["APPROVED", "PARTIALLY_APPROVED", "PAID"].includes(c.status))
          .reduce((s, c) => s + (c.approved_amount || 0), 0),
      });
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, [filterStatus, filterReason]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="insurance-claims">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Insurance Claims</h1>
            <p className="text-slate-500 text-sm mt-0.5">Review and resolve customer claims</p>
          </div>
          <button onClick={load} className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded inline-flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <StatsRow items={[
          { key: "submitted", label: "Submitted", value: stats.submitted, color: "text-amber-600" },
          { key: "review", label: "Under Review", value: stats.review, color: "text-blue-600" },
          { key: "approved", label: "Approved", value: stats.approved, color: "text-emerald-600" },
          { key: "rejected", label: "Rejected", value: stats.rejected, color: "text-rose-600" },
          { key: "paid", label: "Total Paid ₹", value: `₹${stats.paid.toFixed(0)}`, color: "text-slate-700" },
        ]} />

        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-end">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="claims-filter-status"
            className="text-xs border border-slate-200 rounded px-2 py-1.5">
            <option value="">All Statuses</option>
            {["SUBMITTED", "UNDER_REVIEW", "APPROVED", "PARTIALLY_APPROVED", "REJECTED", "PAID"].map((s) =>
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filterReason} onChange={(e) => setFilterReason(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1.5">
            <option value="">All Reasons</option>
            {REASONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="claims-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Claim ID", "AWB", "Customer", "Reason", "Declared ₹", "Claimed ₹", "Max Payout ₹", "Status", "Actions"].map((h) =>
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">No claims.</td></tr>
                : items.map((c) => {
                  const overCap = c.claimed_amount > c.max_payout;
                  return (
                    <tr key={c.claim_id} className="hover:bg-slate-50" data-testid={`claim-row-${c.claim_id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{c.claim_id}</td>
                      <td className="px-3 py-2 font-mono text-xs">{c.awb || "—"}</td>
                      <td className="px-3 py-2 text-xs">{c.customer_id?.slice(-6) || "—"}</td>
                      <td className="px-3 py-2 text-xs">{(c.claim_reason || "").replace(/_/g, " ")}</td>
                      <td className="px-3 py-2 text-xs">₹{(c.declared_value || 0).toFixed(0)}</td>
                      <td className="px-3 py-2 text-xs font-semibold">₹{(c.claimed_amount || 0).toFixed(0)}</td>
                      <td className={`px-3 py-2 text-xs font-semibold ${overCap ? "text-amber-600" : "text-slate-600"}`}
                          title={`Based on ${c.category_code || "category"} insurance rate %`}>
                        ₹{(c.max_payout || 0).toFixed(0)}
                      </td>
                      <td className="px-3 py-2"><StatusPill status={c.status} /></td>
                      <td className="px-3 py-2">
                        <button onClick={() => setSelected(c)}
                          data-testid={`claim-view-${c.claim_id}`}
                          className="text-brand-500 hover:text-brand-700">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <ClaimDetailPanel
          claim={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

function ClaimDetailPanel({ claim, onClose, onChanged }) {
  const [status, setStatus] = useState("APPROVED");
  const [approvedAmount, setApprovedAmount] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isOwner = claim.insurance_type === "OWNER_RISK";
  const maxAmount = Math.min(claim.claimed_amount || 0, claim.max_payout || 0);

  const submit = async () => {
    if (status === "REJECTED" && !rejectionReason.trim()) {
      toast.error("Rejection reason required"); return;
    }
    if (["APPROVED", "PARTIALLY_APPROVED"].includes(status)) {
      const a = parseFloat(approvedAmount || 0);
      if (a <= 0) { toast.error("Approved amount must be > 0"); return; }
      if (a > maxAmount) { toast.error(`Cannot exceed ₹${maxAmount.toFixed(2)}`); return; }
    }
    setBusy(true);
    try {
      const body = { status };
      if (["APPROVED", "PARTIALLY_APPROVED"].includes(status)) {
        body.approved_amount = parseFloat(approvedAmount || 0);
      }
      if (status === "REJECTED") body.rejection_reason = rejectionReason;
      const r = await api.put(`/v2/insurance/claims/${claim.claim_id}/review`, body);
      if (r.data?.refund_credited) {
        toast.success(`₹${(r.data.approved_amount || 0).toFixed(2)} credited to customer wallet`);
      } else {
        toast.success(`Claim ${status.toLowerCase()}`);
      }
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <SlideOver open onClose={onClose} title={`Claim ${claim.claim_id}`} testId="claim-detail-panel">
      <div className="space-y-4 text-sm">
        <div className="flex items-center gap-2">
          <StatusPill status={claim.status} />
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${claim.insurance_type === "CARRIER_RISK" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>
            {(claim.insurance_type || "").replace(/_/g, " ")}
          </span>
        </div>

        {isOwner && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs flex gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>Owner Risk shipment — payout not guaranteed.</div>
          </div>
        )}

        <Section title="Shipment">
          <Row k="AWB" v={<span className="font-mono">{claim.awb || "—"}</span>} />
          <Row k="LR" v={<span className="font-mono">{claim.lr_number || "—"}</span>} />
          <Row k="Category" v={claim.category_code || "—"} />
        </Section>

        <Section title="Amounts">
          <Row k="Declared Value" v={`₹${(claim.declared_value || 0).toFixed(2)}`} />
          <Row k="Claimed Amount" v={<span className="font-semibold">₹{(claim.claimed_amount || 0).toFixed(2)}</span>} />
          <Row k="Max Payout" v={<span className="font-semibold text-emerald-700">₹{(claim.max_payout || 0).toFixed(2)}</span>} />
        </Section>

        <Section title="Reason">
          <div className="inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700 mb-2">
            {(claim.claim_reason || "").replace(/_/g, " ")}
          </div>
          <p className="text-xs text-slate-700 whitespace-pre-wrap">{claim.claim_description}</p>
          {(claim.evidence_references || []).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Evidence</div>
              <ul className="text-xs space-y-0.5">
                {claim.evidence_references.map((r, i) => (
                  <li key={i} className="text-brand-600 underline truncate">{r}</li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {!["APPROVED", "PARTIALLY_APPROVED", "REJECTED", "PAID"].includes(claim.status) && (
          <Section title="Review">
            <div className="space-y-2">
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="claim-review-status"
                className="w-full text-xs border border-slate-200 rounded px-2 py-1.5">
                {["APPROVED", "PARTIALLY_APPROVED", "REJECTED"].map((s) =>
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              {["APPROVED", "PARTIALLY_APPROVED"].includes(status) && (
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Approved Amount (₹)</label>
                  <input type="number" min="0" max={maxAmount} step="0.01"
                    value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)}
                    data-testid="claim-review-amount"
                    className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
                  <p className="text-[10px] text-slate-500 italic mt-1">
                    Cannot exceed ₹{maxAmount.toFixed(2)} (category cap × declared)
                  </p>
                </div>
              )}
              {status === "REJECTED" && (
                <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={3}
                  data-testid="claim-review-rejection"
                  placeholder="Rejection reason (required)"
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
              )}
              <button onClick={submit} disabled={busy}
                data-testid="claim-review-submit"
                className="w-full px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded disabled:opacity-50">
                Submit Review
              </button>
            </div>
          </Section>
        )}
      </div>
    </SlideOver>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <h4 className="text-[10px] font-bold uppercase text-slate-500 mb-2">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800">{v}</span>
    </div>
  );
}
